import React, { createContext, useMemo, useState } from 'react';
import { Alert, Slide, Snackbar } from '@mui/material';
import PropTypes from 'prop-types';

export const NotificationContext = createContext();

// Redesigned (design-feedback) toast styles — mirrors the prototype's
// sonner richColors look. Applied to all notifications system-wide.
const toastPalette = {
  success: {
    bg: '#ecfdf3', border: '#d3fde3', text: '#008a2e', icon: '#16a34a',
  },
  error: {
    bg: '#fff0f0', border: '#ffe0e1', text: '#e60000', icon: '#dc2626',
  },
};

const toastAlertSx = (severity) => {
  const palette = toastPalette[severity] || toastPalette.success;
  return {
    backgroundColor: palette.bg,
    border: `1px solid ${palette.border}`,
    color: palette.text,
    borderRadius: '8px',
    fontSize: 14,
    fontWeight: 500,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    alignItems: 'center',
    padding: '8px 16px',
    '& .MuiAlert-icon': { padding: 0, marginRight: '10px' },
    '& .MuiAlert-message': { padding: 0 },
  };
};

// Icons are sonner's own success/error SVGs (solid circle, cut-out glyph),
// copied verbatim so the toast matches the prototype exactly.
const toastIcon = (severity) => {
  const palette = toastPalette[severity] || toastPalette.success;
  const path = severity === 'error'
    ? 'M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72'
      + ' 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06'
      + ' 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z'
    : 'M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483'
      + ' 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z';
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      fill={palette.icon}
      height="20"
      width="20"
    >
      <path fillRule="evenodd" d={path} clipRule="evenodd" />
    </svg>
  );
};

// Toasts anchor top-right and slide in from the right edge, like the
// prototype's sonner Toaster.
function SlideFromRight(props) {
  // eslint-disable-next-line react/jsx-props-no-spreading
  return <Slide {...props} direction="left" />;
}

export function NotificationProvider({ children }) {
  const [notification, setNotification] = useState(null);
  const [open, setOpen] = useState(false);

  const displayNotification = ({
    severity,
    message,
    autoHideDuration = 6000,
    verticalAlign = 'top',
    horizontalAlign = 'right',
  }) => {
    setNotification({
      autoHideDuration, severity, message, verticalAlign, horizontalAlign,
    });
    setOpen(true);
  };

  // Only closes the Snackbar; the notification content stays mounted until
  // the slide-out transition finishes (see TransitionProps.onExited), so the
  // toast animates back out to the right instead of vanishing.
  const hideNotification = () => setOpen(false);

  const contextValue = useMemo(() => ({
    displayNotification,
  }), []);

  return (
    <NotificationContext.Provider value={contextValue}>
      {notification && (
        <Snackbar
          open={open}
          autoHideDuration={notification?.autoHideDuration}
          onClose={hideNotification}
          anchorOrigin={{ vertical: notification?.verticalAlign, horizontal: notification?.horizontalAlign }}
          TransitionComponent={SlideFromRight}
          TransitionProps={{ onExited: () => setNotification(null) }}
        >
          <Alert
            severity={notification?.severity}
            icon={toastIcon(notification?.severity)}
            sx={toastAlertSx(notification?.severity)}
            data-testid="alert"
          >
            {notification?.message}
          </Alert>
        </Snackbar>
      )}
      {children}
    </NotificationContext.Provider>
  );
}

NotificationProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
